import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width: 400,
    height: 250,
    backgroundColor: theme.palette.background.paper,
    overflow: "auto",
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}));

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

export default function TransferList(props) {
  const { collection, setCollection } = props;
  const menuItems = useSelector((state) => {
    let menuItemsSimplified = state.menuItem.data.map(
      ({ id, name, parentMenuItemId, layoutId, path, childrenId: _childrenId }) => ({
        name,
        parentMenuItemId,
        layoutId,
        path,
        childrenId: id,
        id: 0,
      }),
    );

    menuItemsSimplified = menuItemsSimplified.filter((x) => x.layoutId !== null);
    menuItemsSimplified.sort((a, b) => (a.name !== b.name ? (a.name < b.name ? -1 : 1) : 0));

    return menuItemsSimplified;
  });

  const [initial, setInitial] = useState(false);
  const classes = useStyles();
  const [checked, setChecked] = React.useState([]);

  const [left, setLeft] = React.useState([]);

  const [right, setRight] = React.useState([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (menuItems) => intersection(checked, menuItems).length;

  const handleToggleAll = (menuItems) => () => {
    if (numberOfChecked(menuItems) === menuItems.length) {
      setChecked(not(checked, menuItems));
    } else {
      setChecked(union(checked, menuItems));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  useEffect(() => {
    if (!initial && menuItems && menuItems.length > 0) {
      if (!collection) {
        setLeft(menuItems);
        setInitial(true);

        return;
      }
      if (collection.length === 0) {
        setLeft(menuItems);
        setInitial(true);

        return;
      }
      if (collection && collection.length > 0 && right.length === 0 && left.length === 0) {
        function valExist(item) {
          let response = false;

          collection.forEach((element) => {
            if (element.childrenId === item.childrenId) {
              response = true;
            }
          });

          return response;
        }

        let listLeft = menuItems.filter((item) => !valExist(item));
        let listRight = menuItems.filter((item) => valExist(item));

        setLeft(listLeft);
        setRight(listRight);

        setInitial(true);

        return;
      }
    }
  }, [menuItems, initial, left, right, collection]);

  useEffect(() => {
    setCollection(right);
    left.sort((a, b) => (a.name !== b.name ? (a.name < b.name ? -1 : 1) : 0));
    right.sort((a, b) => (a.name !== b.name ? (a.name < b.name ? -1 : 1) : 0));
  }, [left, right, collection, setCollection]);

  const customList = (title, items) => (
    <Card>
      <CardHeader
        avatar={
          <>
            <Checkbox
              checked={numberOfChecked(items) === items.length && items.length !== 0}
              disabled={items.length === 0}
              indeterminate={
                numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
              }
              inputProps={{ "aria-label": "all items selected" }}
              onClick={handleToggleAll(items)}
            />
          </>
        }
        className={classes.cardHeader}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
        title={title}
      />
      <Divider />
      <List dense className={classes.list} component="div" role="list">
        {items.map((value, index) => {
          const labelId = `transfer-list-all-item-${value}-label-${index}`;

          return (
            <ListItemButton key={`${value}-${index}`} role="listitem" onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  disableRipple
                  checked={checked.indexOf(value) !== -1}
                  inputProps={{ "aria-labelledby": labelId }}
                  tabIndex={-1}
                />
              </ListItemIcon>
              <ListItemText id={value.childrenId} primary={value.name} secondary={value.path} />
            </ListItemButton>
          );
        })}
        <ListItem />
      </List>
    </Card>
  );

  return (
    <>
      {menuItems && menuItems.length > 0 ? (
        <Grid container alignItems="center" className={classes.root} justify="center" spacing={2}>
          <Grid item>{customList("MenuItem Choices", left)}</Grid>
          <Grid item>
            <Grid container alignItems="center" direction="column">
              <Button
                aria-label="move selected right"
                className={classes.button}
                disabled={leftChecked.length === 0}
                size="small"
                variant="outlined"
                onClick={handleCheckedRight}
              >
                &gt;
              </Button>
              <Button
                aria-label="move selected left"
                className={classes.button}
                disabled={rightChecked.length === 0}
                size="small"
                variant="outlined"
                onClick={handleCheckedLeft}
              >
                &lt;
              </Button>
            </Grid>
          </Grid>
          <Grid item>{customList("MenuItem Chosen", right)}</Grid>
        </Grid>
      ) : (
        <Grid>Cargando</Grid>
      )}
    </>
  );
}
